import { useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { deleteSanction, getSanctionByStudent } from "../../../api/etudiants";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { deleteFileAttachementByID } from "../../../api/fileAttachement";
import CreateSanctionModal from "./create_sanction_modal";
import EditSanctionModal from "./edit_sanction_modal";
import withPermission from "../../../core/common/autorisations/withPermission";
import { openSupportInNewTab } from "../../../helper/supportHelper";

type Props = {
  studentId: string;
  studentData: any;
};

// boutton ajout sanction protégée
const ProtectedAddBtn = withPermission(
  (props: any) => (
    <button
      className="btn btn-primary btn-sm"
      onClick={() =>
        props.setOpenSanctionModal((prev: any) => ({
          ...prev,
          create: true,
        }))
      }
    >
      <i className="ti ti-plus me-1" /> Ajouter
    </button>
  ),
  [buildPermission(Resource.STUDENT_SANCTION, Action.CREATE)],
  "masquer",
);

// btn modifier sanction protégée
const ProtectedEditBtn = withPermission(
  (props: any) => (
    <li>
      <Link
        className="dropdown-item rounded-1"
        to="#"
        onClick={() => {
          props.setSelectedSanction(props.sanction);
          props.setOpenSanctionModal((prev: any) => ({
            ...prev,
            edit: true,
          }));
        }}
      >
        <i className="ti ti-edit me-2" />
        Modifier
      </Link>
    </li>
  ),
  [buildPermission(Resource.STUDENT_SANCTION, Action.UPDATE)],
  "masquer",
);

// btn supprimer sanction protégée
const ProtectedDeleteBtn = withPermission(
  (props: any) => (
    <li>
      <Link
        className="dropdown-item rounded-1 text-danger"
        to="#"
        onClick={() => props.confirmDeleteSanction(props.sanction)}
      >
        <i className="ti ti-trash me-2" />
        Supprimer
      </Link>
    </li>
  ),
  [buildPermission(Resource.STUDENT_SANCTION, Action.DELETE)],
  "masquer",
);

// btn read piéces jointes
const ProtectedBtnReadLinkedPiece = withPermission(
  (props: any) => (
    <button
      className="btn btn-sm btn-outline-primary"
      title="Voir"
      onClick={() =>
        props.handleGetSanctionFile(props.file.id, props.file.filename)
      }
      disabled={props.filesLoading[props.file.id]}
    >
      {props.filesLoading[props.file.id] === "open" ? (
        <span className="spinner-border spinner-border-sm" />
      ) : (
        <i className="ti ti-eye" />
      )}
    </button>
  ),
  [buildPermission(Resource.STUDENT_SANCTION, Action.READ)],
  "masquer",
);

// btn delete piéces jointes
const ProtectedBtnDeleteLinkedPiece = withPermission(
  (props: any) => (
    <button
      className="btn btn-sm btn-outline-danger"
      title="Supprimer"
      onClick={() => props.handleDeleteSanctionFile(props.file.id)}
      disabled={props.filesLoading[props.file.id]}
    >
      {props.filesLoading[props.file.id] === "delete" ? (
        <span className="spinner-border spinner-border-sm" />
      ) : (
        <i className="ti ti-trash" />
      )}
    </button>
  ),
  [buildPermission(Resource.STUDENT_SANCTION, Action.UPDATE)],
  "masquer",
);

const StudentSanctionDetails = ({ studentId, studentData }: Props) => {
  const MySwal = withReactContent(Swal);
  const [sanctions, setSanctions] = useState<any[]>([]);
  const [selectedSanction, setSelectedSanction] = useState<any>();
  const [openSanctionModal, setOpenSanctionModal] = useState({
    create: false,
    edit: false,
  });
  const [filesLoading, setFilesLoading] = useState<any>({});

  const fetchSanctions = async () => {
    if (!studentId) return;
    try {
      const response = await getSanctionByStudent(
        studentId,
        buildPermissionContext(Resource.STUDENT, Action.READ),
      );
      setSanctions(response.data || []);
    } catch (error: any) {
      // console.error("Erreur chargement sanctions:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    fetchSanctions();
  }, [studentId]);

  const handleDeleteSanctionFile = async (fileId: string) => {
    try {
      setFilesLoading((prev: any) => ({ ...prev, [fileId]: "delete" }));
      await deleteFileAttachementByID(
        fileId,
        buildPermissionContext(Resource.FILE_ATTACHEMENT, Action.DELETE),
      );
      toast.success("Fichier supprimé");
      fetchSanctions();
    } catch (error: any) {
      // console.log("Erreur lors de la suppression du fichier: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setFilesLoading((prev: any) => ({ ...prev, [fileId]: "" }));
    }
  };

  const handleGetSanctionFile = async (fileId: string, fileName: string) => {
    try {
      setFilesLoading((prev: any) => ({ ...prev, [fileId]: "open" }));

      await openSupportInNewTab({
        entityType: "Sanction",
        fileName,
        context: buildPermissionContext(Resource.STUDENT_SANCTION, Action.READ),
      });
    } catch (error: any) {
      // console.log("Erreur lors de l'ouverture du fichier: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setFilesLoading((prev: any) => ({ ...prev, [fileId]: "" }));
    }
  };

  const confirmDeleteSanction = (sanction: any) => {
    MySwal.fire({
      title: "Supprimer sanction ?",
      text: "Voulez-vous vraiment supprimer cette sanction ?",
      showCancelButton: true,
      confirmButtonText: "Supprimer",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          await deleteSanction(
            sanction.id,
            buildPermissionContext(Resource.STUDENT_SANCTION, Action.DELETE),
          );
          MySwal.fire("Supprimé !", "Sanction supprimée.", "success");
          setSanctions((prev) => prev.filter((s) => s.id !== sanction.id));
        } catch (error) {
          MySwal.fire(
            "Erreur",
            "Impossible de supprimer la sanction.",
            "error",
          );
        }
      }
    });
  };

  return (
    <>
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5>Sanctions Disciplinaires</h5>
          <ProtectedAddBtn setOpenSanctionModal={setOpenSanctionModal} />
        </div>
        <div className="card-body">
          {sanctions.length === 0 ? (
            <div className="text-center text-muted p-4">
              Aucune sanction enregistrée.
            </div>
          ) : (
            sanctions.map((sanction: any) => (
              <div
                key={sanction.id}
                className={`border-start border-3 shadow-sm p-3 mb-3 ${(() => {
                  if (sanction.type === "Conseil Disciplinaire")
                    return "border-danger";
                  switch (sanction.sanction_received) {
                    case "Exclusion Définitive":
                      return "border-danger";
                    case "Exclusion Temporaire":
                      return "border-warning";
                    case "Blâme":
                      return "border-skyblue";
                    default:
                      return "border-secondary";
                  }
                })()}`}
              >
                <div className="d-flex align-items-center mb-3 pb-3 border-bottom">
                  <span
                    className={`avatar p-1 me-2 flex-shrink-0 ${(() => {
                      if (sanction.type === "Conseil Disciplinaire")
                        return "bg-danger-transparent";
                      switch (sanction.sanction_received) {
                        case "Exclusion Définitive":
                          return "bg-danger-transparent";
                        case "Exclusion Temporaire":
                          return "bg-warning-transparent";
                        case "Blâme":
                          return "bg-teal-transparent";
                        default:
                          return "bg-secondary-transparent";
                      }
                    })()}`}
                  >
                    <i
                      className={`fs-20 ${(() => {
                        if (sanction.type === "Conseil Disciplinaire")
                          return "ti ti-user-x text-danger";
                        switch (sanction.sanction_received) {
                          case "Exclusion Définitive":
                            return "ti ti-user-x text-danger";
                          case "Exclusion Temporaire":
                            return "ti ti-user-minus text-warning";
                          case "Blâme":
                            return "ti ti-user-edit text-info";
                          default:
                            return "ti ti-user text-muted";
                        }
                      })()}`}
                    />
                  </span>
                  <div className="flex-fill">
                    <h6 className="mb-1">
                      {sanction.type === "Sanction Normale" ? "Sanction Disciplinaire" : sanction.type}
                      {sanction.cancels && (
                        <span className="badge badge-soft-danger ms-2">
                          Annulée
                        </span>
                      )}
                    </h6>
                    <p className="d-flex align-items-center text-muted small">
                      <i className="ti ti-calendar me-1" />
                      {new Date(sanction.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="dropdown">
                    <Link
                      to="#"
                      className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="ti ti-dots-vertical fs-14" />
                    </Link>
                    <ul className="dropdown-menu dropdown-menu-end p-2">
                      {/* Btn protéger pour la modification des sanctions */}
                      <ProtectedEditBtn
                        sanction={sanction}
                        setSelectedSanction={setSelectedSanction}
                        setOpenSanctionModal={setOpenSanctionModal}
                      />
                      {/* Btn protéger pour la suppression des sanctions */}
                      <ProtectedDeleteBtn
                        sanction={sanction}
                        confirmDeleteSanction={confirmDeleteSanction}
                      />
                    </ul>
                  </div>
                </div>

                {sanction.reason_sanction && (
                  <div className="mb-3">
                    <p className="mb-1 text-muted fw-semibold small">
                      Raison :
                    </p>
                    <p className="mb-0 text-break">
                      {sanction.reason_sanction}
                    </p>
                  </div>
                )}

                <div className="d-flex align-items-center justify-content-between text-muted small">
                  <p className="mb-0">
                    <i className="ti ti-clock me-1" />
                    {sanction.sanction_received} · {sanction.duration}
                  </p>
                </div>

                {sanction.files?.length > 0 && (
                  <div className="mt-3 pt-3 border-top">
                    <h6 className="mb-2 text-muted small">Pièces jointes :</h6>
                    <div className="d-flex flex-column gap-2">
                      {sanction.files.map((file: any) => (
                        <div
                          key={file.id}
                          className="d-flex align-items-center justify-content-between p-2 bg-light rounded"
                        >
                          <div className="d-flex align-items-center overflow-hidden">
                            <i className="ti ti-paperclip text-primary me-2 flex-shrink-0" />
                            <span
                              className="text-truncate"
                              title={file.filename}
                              style={{ maxWidth: "200px" }}
                            >
                              {file.filename}
                            </span>
                          </div>
                          <div className="d-flex gap-2">
                            <ProtectedBtnReadLinkedPiece
                              file={file}
                              handleGetSanctionFile={handleGetSanctionFile}
                              filesLoading={filesLoading}
                            />
                            <ProtectedBtnDeleteLinkedPiece
                              file={file}
                              filesLoading={filesLoading}
                              handleDeleteSanctionFile={
                                handleDeleteSanctionFile
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <CreateSanctionModal
        show={openSanctionModal.create}
        metaData={{ Student: studentData }}
        onSaved={fetchSanctions}
        onClose={() =>
          setOpenSanctionModal((prev) => ({ ...prev, create: false }))
        }
      />
      <EditSanctionModal
        show={openSanctionModal.edit}
        metaData={{ Student: studentData }}
        sanction={selectedSanction}
        onSaved={fetchSanctions}
        onClose={() =>
          setOpenSanctionModal((prev) => ({ ...prev, edit: false }))
        }
      />
    </>
  );
};

export default StudentSanctionDetails;
